// Touch, mouse and the phone's orientation for the DOM windows (core/render-dom.js). The windows take their own
// taps, typing and scrolling as any page does; what is here is what sits around them:
//
// - Snap: a sideways swipe follows the finger and moves to the next window (or springs back); up and down stays the
//   window's own scrolling (touch-action: pan-y), and a strip that scrolls sideways keeps its own swipe. A trackpad's
//   sideways swipe or the arrow keys step too.
// - Look: the phone's orientation turns the view (yaw and pitch, from the same sum the room's camera used); on a
//   desktop, dragging the background turns it. On a phone the dot and its taps are core/look-dot.js (a double tap
//   brings a window up, render-dom.js zoomTo); pinching in, or ‹ Back, puts it back. (The first tap on a window used to
//   bring it up here, held from its pointerdown to its click — a touch that became a pan never clicked, and Snap's
//   next tap was swallowed into bringing a window up there, Josh 10-17.)
// - visionOS's hover and press: `.hover` on what the mouse rests on, `.pressed` while a finger or button is down.
// - A streamed Mac window (Josh 09-28: "one finger drag to move mouse and tap to click"): on a touch screen one finger
//   moves the Mac's pointer from where it is and a tap clicks there, a press held for a moment then moved drags, two
//   fingers scroll; with a mouse the pointer is where the mouse is, a click clicks. A stream inside a pane is pressed
//   where it is touched (it is small among other things). The keys follow a click (core/streamkeys.js).
//
// One seam: `new DomInput(world)`; the world calls `stream(rec)`, `embed(spec)`, `unembed(spec)`, `drawPointer(spec)`,
// `lookOn()`, `lookOff()`, `recalibrate()`.

const ACTOR = 'button, a, [data-act], input, textarea, select, [role="button"], li[data-row]'
const coarse = () => { try { return matchMedia('(pointer: coarse)').matches } catch { return false } }
const HOLD_MS = 380

export class DomInput {
  constructor(world) {
    this.w = world
    this.app = world.app
    this.embeds = new Map()   // spec → its canvas's listeners
    const stage = world.stage
    this.hoverEl = null
    this.pressedEl = null
    // hover (a mouse resting) and press, as htmlrender drew them
    stage.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse' && !e.buttons) this.hover(e.target.closest?.(ACTOR)) })
    stage.addEventListener('pointerleave', () => this.hover(null))
    stage.addEventListener('pointerdown', (e) => {
      const el = e.target.closest?.(ACTOR)
      if (el && !el.disabled) { this.pressedEl = el; el.classList.add('pressed') }
      // a press anywhere but a stream's picture takes the keys off the stream
      if (!e.target.closest?.('.picture, .dw-embed')) this.app.streamKeys?.elsewhere(null)
    }, true)
    const unpress = () => { this.pressedEl?.classList.remove('pressed'); this.pressedEl = null }
    addEventListener('pointerup', unpress, true)
    addEventListener('pointercancel', unpress, true)
    this.swipes()
    this.lookGestures()
    addEventListener('keydown', (e) => {
      if (this.w.suspended || this.w.mode !== 'snap') return
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement || e.target?.isContentEditable) return
      if (e.key === 'ArrowLeft') this.w.step(-1)
      else if (e.key === 'ArrowRight') this.w.step(1)
    })
  }

  hover(el) {
    if (el === this.hoverEl) return
    this.hoverEl?.classList.remove('hover')
    this.hoverEl = el && !el.disabled ? el : null
    this.hoverEl?.classList.add('hover')
  }

  // MARK: Snap — swiping along the row

  swipes() {
    const stage = this.w.stage
    let s = null
    // does something under the finger scroll sideways itself? then the swipe is its own
    const scrollsSideways = (el) => {
      for (; el && el !== stage; el = el.parentElement) {
        if (el.scrollWidth > el.clientWidth + 2 && /(auto|scroll)/.test(getComputedStyle(el).overflowX)) return true
      }
      return false
    }
    // touch events, not pointer events: a scrolling region inside a window takes the pointer away (pointercancel) as
    // soon as the browser pans, but its touches still arrive — a sideways swipe over the transcript must still step
    const point = (e) => e.touches[0] || e.changedTouches[0]
    stage.addEventListener('touchstart', (e) => {
      if (this.w.mode !== 'snap' || this.w.suspended) return
      if (e.touches.length !== 1) { if (s?.swiping) this.w.slide(0, true); s = null; return }
      if (e.target.closest?.('.picture, .dw-embed')) return   // a stream takes the finger as the Mac's pointer
      const p = point(e)
      s = { x: p.clientX, y: p.clientY, t: performance.now(), swiping: false, decided: false, own: scrollsSideways(e.target) }
    }, { passive: true })
    stage.addEventListener('touchmove', (e) => {
      if (!s || e.touches.length !== 1) return
      const p = point(e), dx = p.clientX - s.x, dy = p.clientY - s.y
      if (!s.decided) {
        if (Math.abs(dx) < 12 && Math.abs(dy) < 12) return
        s.decided = true
        // sideways, and not over something that scrolls sideways itself: the row follows the finger
        s.swiping = !s.own && Math.abs(dx) > Math.abs(dy) * 1.3
        if (s.swiping) this.pressedEl?.classList.remove('pressed')
      }
      if (s.swiping) this.w.slide(dx, false)
    }, { passive: true })
    const end = (e) => {
      if (!s) return
      const was = s; s = null
      if (!was.swiping) return
      const p = point(e), dx = p.clientX - was.x, W = stage.clientWidth || innerWidth, fast = Math.abs(dx) / Math.max(1, performance.now() - was.t) > 0.5
      // the swipe was no tap: the click it would make is swallowed
      const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault() }
      addEventListener('click', swallow, { capture: true, once: true })
      setTimeout(() => removeEventListener('click', swallow, true), 400)
      if (e.type !== 'touchcancel' && (Math.abs(dx) > W * 0.18 || (fast && Math.abs(dx) > 30))) this.w.step(dx < 0 ? 1 : -1)
      else this.w.slide(0, true)
    }
    stage.addEventListener('touchend', end)
    stage.addEventListener('touchcancel', end)
    // a trackpad's sideways swipe steps (its up and down scrolls the window)
    let wheelAt = 0
    stage.addEventListener('wheel', (e) => {
      if (this.w.mode !== 'snap' || Math.abs(e.deltaX) < 30 || Math.abs(e.deltaX) < Math.abs(e.deltaY) * 1.5) return
      if (scrollsSideways(e.target) || e.target.closest?.('.picture, .dw-embed')) return
      const now = performance.now(); if (now - wheelAt < 700) return
      wheelAt = now; this.w.step(e.deltaX > 0 ? 1 : -1)
    }, { passive: true })
  }

  // MARK: Look — orientation, dragging, pinching a window back

  /// Device orientation where it exists: true when events arrive within a second (iOS asks once, from the tap).
  lookOn() {
    this.lookOff()
    return new Promise((resolve) => {
      if (typeof DeviceOrientationEvent === 'undefined') return resolve(false)
      const listen = () => {
        let got = false
        const onEvent = (e) => {
          if (e.alpha == null) return
          got = true
          const { yaw, pitch } = viewOf(e)
          if (this.yawOffset == null) this.yawOffset = (this.w.current?.angle || 0) - yaw
          if (this.w.mode !== 'look' || this.w.zoomed || this.w.suspended) return
          this.w.yaw = yaw + this.yawOffset; this.w.pitch = pitch
          if (!this.queued) { this.queued = requestAnimationFrame(() => { this.queued = null; this.w.aim() }) }
        }
        addEventListener('deviceorientation', onEvent)
        this.stopOrientation = () => removeEventListener('deviceorientation', onEvent)
        setTimeout(() => { if (!got) { this.stopOrientation?.(); this.stopOrientation = null } this.oriented = got; resolve(got) }, 1000)
      }
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission().then((state) => (state === 'granted' ? listen() : resolve(false))).catch(() => resolve(false))
      } else listen()
    })
  }
  lookOff() { this.stopOrientation?.(); this.stopOrientation = null; this.yawOffset = null; this.oriented = false }
  /// Whichever way the phone points now becomes "toward the window in front".
  recalibrate() { this.yawOffset = null }

  lookGestures() {
    const stage = this.w.stage
    let drag = null
    const pinch = new Map()
    let spread = 0
    stage.addEventListener('pointerdown', (e) => {
      if (this.w.mode !== 'look') return
      pinch.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (pinch.size === 2) { const [a, b] = [...pinch.values()]; spread = Math.hypot(a.x - b.x, a.y - b.y) }
      // on a desktop (no orientation), the background turns the view
      if (!this.oriented && e.pointerType === 'mouse' && !e.target.closest?.('.dw-slot')) drag = { x: e.clientX, y: e.clientY, yaw: this.w.yaw, pitch: this.w.pitch }
    })
    addEventListener('pointermove', (e) => {
      if (pinch.has(e.pointerId)) pinch.set(e.pointerId, { x: e.clientX, y: e.clientY })
      // fingers coming together over a window that is up: it goes back
      if (pinch.size === 2 && this.w.zoomed && spread) {
        const [a, b] = [...pinch.values()]
        if (Math.hypot(a.x - b.x, a.y - b.y) < spread * 0.7) { spread = 0; this.w.unzoom() }
      }
      if (!drag || this.w.mode !== 'look') return
      this.w.yaw = drag.yaw + (e.clientX - drag.x) * 0.15
      this.w.pitch = Math.max(-60, Math.min(60, drag.pitch + (e.clientY - drag.y) * 0.15))
      this.w.aim()
    })
    const up = (e) => { pinch.delete(e.pointerId); if (pinch.size < 2) spread = 0; drag = null }
    addEventListener('pointerup', up)
    addEventListener('pointercancel', up)
  }

  // MARK: streams

  /// A stream window: its picture is the Mac's (see the top of this file).
  stream(rec) {
    const spec = rec.spec, root = rec.el
    const at = (e) => {
      const pic = root.querySelector('.picture canvas') || root.querySelector('.picture')
      const r = pic?.getBoundingClientRect()
      return r?.width ? { x: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)), y: Math.max(0, Math.min(1, (e.clientY - r.top) / r.height)), r } : null
    }
    const onPicture = (e) => !!e.target.closest?.('.picture') && !e.target.closest?.('.note, .listen, .ornament, .menu, .tip')
    const fingers = new Map()
    let press = null, scroll = null
    const down = (e) => {
      if (!onPicture(e)) return
      e.preventDefault()
      const p = at(e); if (!p) return
      try { e.target.setPointerCapture(e.pointerId) } catch {}
      fingers.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (fingers.size === 2) {
        // two fingers scroll: whatever one finger began is let go
        if (press?.dragging) this.app.sendPointer(spec, 'up')
        clearTimeout(press?.hold); press = null
        scroll = this.middle(fingers)
        return
      }
      if (fingers.size > 2) return
      if (e.pointerType === 'mouse') {
        spec.ptr.x = p.x; spec.ptr.y = p.y
        this.app.sendPointer(spec, 'move'); this.app.sendPointer(spec, 'down')
        press = { mouse: true }
      } else {
        press = { x: e.clientX, y: e.clientY, moved: false, dragging: false, r: p.r }
        press.hold = setTimeout(() => { if (press && !press.moved) { press.dragging = true; this.app.sendPointer(spec, 'down') } }, HOLD_MS)
      }
      this.drawPointer(spec)
    }
    const move = (e) => {
      if (fingers.has(e.pointerId)) fingers.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (scroll && fingers.size === 2) {
        const m = this.middle(fingers)
        // the content follows the fingers: a positive dy scrolls the Mac's content down, as a pinch-drag in the
        // headset's Scroll mode does (the wheel below is the other way: a wheel's deltaY is the page's, not the hand's)
        this.app.sendPointer(spec, 'scroll', { dx: m.x - scroll.x, dy: m.y - scroll.y })
        scroll = m
        return
      }
      if (e.pointerType === 'mouse') {
        if (!onPicture(e) && !press) return
        const p = at(e); if (!p) return
        spec.ptr.x = p.x; spec.ptr.y = p.y
        // the Mac's pointer follows the mouse while it hovers (the window's Mouse hover choice), and while it drags
        if (press || spec.chromeSpec?.dom?.prefs?.forwardsPointer !== false) this.throttledMove(spec)
        return
      }
      if (!press || press.mouse) return
      const dx = e.clientX - press.x, dy = e.clientY - press.y
      press.x = e.clientX; press.y = e.clientY
      if (Math.abs(dx) + Math.abs(dy) > 1) press.moved = true
      if (press.moved && !press.dragging) clearTimeout(press.hold)
      // the pointer keeps pace with the finger over the picture as it is on screen
      spec.ptr.x = Math.max(0, Math.min(1, spec.ptr.x + dx / Math.max(40, press.r.width)))
      spec.ptr.y = Math.max(0, Math.min(1, spec.ptr.y + dy / Math.max(40, press.r.height)))
      this.throttledMove(spec)
    }
    const up = (e) => {
      if (!fingers.has(e.pointerId)) return
      fingers.delete(e.pointerId)
      if (fingers.size < 2) scroll = null
      if (!press) return
      const was = press; press = null
      if (was.mouse) { this.app.sendPointer(spec, 'up'); this.app.streamKeys?.take(spec, e); return }
      clearTimeout(was.hold)
      if (e.type === 'pointercancel') { if (was.dragging) this.app.sendPointer(spec, 'up'); return }
      if (was.dragging) this.app.sendPointer(spec, 'up')
      else if (!was.moved) { this.app.sendPointer(spec, 'down'); this.app.sendPointer(spec, 'up') }   // a tap: a click where the pointer is
      this.app.streamKeys?.take(spec, e)
    }
    const wheel = (e) => {
      if (!onPicture(e)) return
      e.preventDefault()
      const p = at(e); if (p && e.pointerType !== 'touch') { spec.ptr.x = p.x; spec.ptr.y = p.y }
      this.app.sendPointer(spec, 'scroll', { dx: -e.deltaX, dy: -e.deltaY })
    }
    root.addEventListener('pointerdown', down)
    root.addEventListener('pointermove', move)
    root.addEventListener('pointerup', up)
    root.addEventListener('pointercancel', up)
    root.addEventListener('wheel', wheel, { passive: false })
    rec.input = { dispose: () => { root.removeEventListener('pointerdown', down); root.removeEventListener('pointermove', move); root.removeEventListener('pointerup', up); root.removeEventListener('pointercancel', up); root.removeEventListener('wheel', wheel) } }
  }
  middle(fingers) { const all = [...fingers.values()]; return { x: all.reduce((t, p) => t + p.x, 0) / all.length, y: all.reduce((t, p) => t + p.y, 0) / all.length } }
  throttledMove(spec) {
    if (spec.moveQueued) return
    spec.moveQueued = requestAnimationFrame(() => { spec.moveQueued = null; this.app.sendPointer(spec, 'move'); this.drawPointer(spec) })
  }

  /// The Mac's pointer drawn over the picture where the app has put it (the Mac keeps its own out of the picture).
  drawPointer(spec) {
    const root = spec.chromeSpec?.dom?.root
    const slot = root?.querySelector('.picture .slot')
    if (!slot || !spec.ptr) return
    let dot = slot.querySelector('.dw-ptr')
    if (!dot) { dot = document.createElement('i'); dot.className = 'dw-ptr'; slot.appendChild(dot) }
    dot.style.left = (spec.ptr.x * 100) + '%'
    dot.style.top = (spec.ptr.y * 100) + '%'
    dot.hidden = !coarse()   // with a mouse the Mac window's own pointer is the mouse
  }

  /// A stream inside a pane: pressed where it is touched; held or moved, it drags; the wheel scrolls there.
  embed(spec) {
    if (this.embeds.has(spec)) return
    const c = spec.canvas
    let press = null
    const at = (e) => { const r = c.getBoundingClientRect(); return r.width ? { x: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)), y: Math.max(0, Math.min(1, (e.clientY - r.top) / r.height)) } : null }
    const set = (p) => { if (p) { spec.ptr.x = p.x; spec.ptr.y = p.y } }
    const down = (e) => {
      e.preventDefault(); e.stopPropagation()
      try { c.setPointerCapture(e.pointerId) } catch {}
      set(at(e)); this.app.sendPointer(spec, 'move')
      press = { x: e.clientX, y: e.clientY, dragging: false }
      press.hold = setTimeout(() => { if (press && !press.dragging) { press.dragging = true; this.app.sendPointer(spec, 'down') } }, HOLD_MS)
    }
    const move = (e) => {
      if (!press) return
      if (!press.dragging && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 8) { clearTimeout(press.hold); press.dragging = true; this.app.sendPointer(spec, 'down') }
      set(at(e)); this.throttledMove(spec)
    }
    const up = (e) => {
      if (!press) return
      const was = press; press = null
      clearTimeout(was.hold)
      set(at(e))
      if (was.dragging) this.app.sendPointer(spec, 'up')
      else if (e.type !== 'pointercancel') { this.app.sendPointer(spec, 'down'); this.app.sendPointer(spec, 'up') }
      this.app.streamKeys?.take(spec, e)
    }
    const wheel = (e) => { e.preventDefault(); set(at(e)); this.app.sendPointer(spec, 'scroll', { dx: -e.deltaX, dy: -e.deltaY }) }
    c.addEventListener('pointerdown', down); c.addEventListener('pointermove', move); c.addEventListener('pointerup', up); c.addEventListener('pointercancel', up)
    c.addEventListener('wheel', wheel, { passive: false })
    this.embeds.set(spec, () => { c.removeEventListener('pointerdown', down); c.removeEventListener('pointermove', move); c.removeEventListener('pointerup', up); c.removeEventListener('pointercancel', up); c.removeEventListener('wheel', wheel) })
  }
  unembed(spec) { this.embeds.get(spec)?.(); this.embeds.delete(spec) }
}

/// Where the phone's back camera looks, from its orientation: yaw (degrees, left positive) and pitch (up positive) —
/// the room's old camera sum (YXZ Euler of beta, alpha, -gamma; then the camera's own quarter turn and the screen's turn).
export function viewOf(e) {
  const rad = Math.PI / 180
  const alpha = (e.alpha || 0) * rad, beta = (e.beta || 0) * rad, gamma = (e.gamma || 0) * rad
  const orient = ((globalThis.screen?.orientation?.angle ?? globalThis.orientation ?? 0) * rad)
  let q = euler(beta, alpha, -gamma)
  q = mul(q, [-Math.SQRT1_2, 0, 0, Math.SQRT1_2])
  q = mul(q, axis(0, 0, 1, -orient))
  const f = rotate(q, [0, 0, -1])
  return { yaw: Math.atan2(-f[0], -f[2]) / rad, pitch: Math.asin(Math.max(-1, Math.min(1, f[1]))) / rad }
}
// quaternions as [x, y, z, w]
function euler(x, y, z) {   // three's Euler 'YXZ' → quaternion
  const c1 = Math.cos(x / 2), c2 = Math.cos(y / 2), c3 = Math.cos(z / 2), s1 = Math.sin(x / 2), s2 = Math.sin(y / 2), s3 = Math.sin(z / 2)
  return [s1 * c2 * c3 + c1 * s2 * s3, c1 * s2 * c3 - s1 * c2 * s3, c1 * c2 * s3 - s1 * s2 * c3, c1 * c2 * c3 + s1 * s2 * s3]
}
function axis(x, y, z, a) { const s = Math.sin(a / 2); return [x * s, y * s, z * s, Math.cos(a / 2)] }
function mul(a, b) {
  const [ax, ay, az, aw] = a, [bx, by, bz, bw] = b
  return [ax * bw + aw * bx + ay * bz - az * by, ay * bw + aw * by + az * bx - ax * bz, az * bw + aw * bz + ax * by - ay * bx, aw * bw - ax * bx - ay * by - az * bz]
}
function rotate(q, v) {
  const [x, y, z, w] = q, [vx, vy, vz] = v
  const ix = w * vx + y * vz - z * vy, iy = w * vy + z * vx - x * vz, iz = w * vz + x * vy - y * vx, iw = -x * vx - y * vy - z * vz
  return [ix * w + iw * -x + iy * -z - iz * -y, iy * w + iw * -y + iz * -x - ix * -z, iz * w + iw * -z + ix * -y - iy * -x]
}
